import type { CardLayer, SlotDesign } from './design-data';

/**
 * Fit a slot designed at one page size onto a product page of another size.
 *
 * The editor always forces the PRODUCT's printSpec onto a template or restored
 * draft (EditorShell `applyTemplateToSlots`, the design page's draft
 * reconciliation). That used to change only `canvas` and leave every layer at
 * its old millimetre position, which is harmless while both are the same size
 * but turns an A6 design on an A5 page into a small card stuck in the top-left
 * corner. Invitations are moving from A6 (105 × 148) to A5 (148 × 210), and the
 * backend migrates its templates while browsers still hold A6 drafts, so the
 * two can disagree in either order for a while.
 *
 * Same rules as the backend migration
 * (photo-upload-backend/src/core/template/invitation-print-size.ts):
 *   - transform x / width × sx, y / height × sy, rotation kept;
 *   - text size, shape strokeWidth / radiusMm, svg layer strokeWidth × sx (the
 *     width factor, so text keeps its line breaks);
 *   - bleed, safe margin, viewBox units, crops and fractions kept.
 *
 * Only a pure RESIZE of the same shape is scaled (aspect ratios within 3 %).
 * A different shape (portrait design on a landscape page) cannot be scaled
 * without distorting it, so there the old behaviour stays: the canvas is set,
 * the layers are left where they were.
 */
export interface PageSize {
  widthMm: number;
  heightMm: number;
}

const ASPECT_TOLERANCE = 0.03;

export function isSameShape(a: PageSize, b: PageSize): boolean {
  if (!(a.widthMm > 0 && a.heightMm > 0 && b.widthMm > 0 && b.heightMm > 0)) return false;
  const ra = a.widthMm / a.heightMm;
  const rb = b.widthMm / b.heightMm;
  return Math.abs(ra - rb) / rb <= ASPECT_TOLERANCE;
}

const round6 = (v: number) => Math.round(v * 1e6) / 1e6;

function scaleLayer(layer: CardLayer, sx: number, sy: number): CardLayer {
  const t = layer.transform;
  const transform = t
    ? {
        ...t,
        x: round6(t.x * sx),
        y: round6(t.y * sy),
        width: round6(t.width * sx),
        height: round6(t.height * sy),
      }
    : t;
  const uniform = sx;
  switch (layer.type) {
    case 'text':
      return { ...layer, transform, size: round6(layer.size * uniform) };
    case 'shape':
      return {
        ...layer,
        transform,
        ...(typeof layer.strokeWidth === 'number' ? { strokeWidth: round6(layer.strokeWidth * uniform) } : {}),
        ...(typeof layer.radiusMm === 'number' ? { radiusMm: round6(layer.radiusMm * uniform) } : {}),
      };
    case 'svg':
      return {
        ...layer,
        transform,
        ...(typeof layer.strokeWidth === 'number' ? { strokeWidth: round6(layer.strokeWidth * uniform) } : {}),
      };
    default:
      return { ...layer, transform };
  }
}

/**
 * Return `slot` placed on `target` (canvas dimensions, bleed and safe margin
 * from the target spec). Layers are scaled when the slot was designed at a
 * different size of the same shape; otherwise they are left untouched.
 * Never mutates its input.
 */
export function fitSlotToCanvas(
  slot: SlotDesign,
  target: { widthMm: number; heightMm: number; bleedMm: number; safeMm?: number },
): SlotDesign {
  const canvas = {
    widthMm: target.widthMm,
    heightMm: target.heightMm,
    bleedMm: target.bleedMm,
    safeMm: target.safeMm,
  };
  const from = slot.canvas;
  const differs =
    !!from && (from.widthMm !== target.widthMm || from.heightMm !== target.heightMm);
  if (!differs || !isSameShape(from, target)) {
    return { ...slot, canvas };
  }
  const sx = target.widthMm / from.widthMm;
  const sy = target.heightMm / from.heightMm;
  return {
    ...slot,
    canvas,
    layers: (slot.layers ?? []).map((l) => scaleLayer(l, sx, sy)),
  };
}
